import { stagger } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icon';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import type { UsageLimits } from '@/api/schemasUsage';
import { meterViews, type MeterView } from './usageModel';

const BAR: Record<MeterView['tone'], string> = { succeeded: 'bg-succeeded', awaiting: 'bg-awaiting', failed: 'bg-failed', subtle: 'bg-subtle' };
const TEXT: Record<MeterView['tone'], string> = { succeeded: 'text-succeeded', awaiting: 'text-awaiting', failed: 'text-failed', subtle: 'text-subtle' };

/** One meter: label, used of limit, the bar in its level's token, the percent and what it counts (only what was used without a plan of the org's own). */
function MeterCard({ meter, index }: { meter: MeterView; index: number }) {
  return (
    <Card data-testid={`usage-meter-${meter.id}`} data-level={meter.level} className="enter gap-0 p-4 sm:p-5" style={stagger(index + 2)}>
      <div className="flex items-center justify-between gap-2 text-sm text-muted-foreground">
        <span className="truncate">{meter.label}</span>
        <Icon name={meter.icon} size={18} className="text-subtle" />
      </div>
      <div className="tabular mt-3 break-words font-mono text-lg font-semibold leading-snug text-foreground">
        {meter.amount}
      </div>
      {meter.consumptionOnly ? null : meter.bar === null ? (
        <div aria-hidden className="mt-4 h-1.5 rounded-full border border-dashed border-border-strong" />
      ) : (
        <Progress value={meter.bar} aria-label={`${meter.label}: ${meter.percentLabel} of the plan`} className="mt-4" indicatorClassName={BAR[meter.tone]} />
      )}
      <div className="mt-3 flex items-start justify-between gap-3 text-xs">
        <span className="text-subtle">{meter.caption}</span>
        {meter.consumptionOnly ? null : <span className={cn('tabular shrink-0 font-mono font-semibold', TEXT[meter.tone])}>{meter.percentLabel}</span>}
      </div>
    </Card>
  );
}

/** Four skeleton cards with the loaded cards' geometry (no layout jump when the data arrives). */
export function MeterCardsSkeleton() {
  return (
    <div data-testid="usage-meters-loading" aria-busy="true" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {[0, 1, 2, 3].map((i) => (
        <Card key={i} className="gap-0 p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="size-[18px]" />
          </div>
          <Skeleton className="mt-3 h-6 w-36" />
          <Skeleton className="mt-4 h-1.5 w-full rounded-full" />
          <div className="mt-3 flex justify-between gap-3">
            <Skeleton className="h-3.5 w-32" />
            <Skeleton className="h-3.5 w-8" />
          </div>
        </Card>
      ))}
    </div>
  );
}

export function MeterCards({ limits }: { limits: UsageLimits }) {
  return (
    <div data-testid="usage-meters" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {meterViews(limits).map((m, i) => (
        <MeterCard key={m.id} meter={m} index={i} />
      ))}
    </div>
  );
}
